import React from "react";
import { Modal } from "antd";
import "./style.css";

const ConfirmationPopup = ({
  visible = false,
  open, // Support both visible and open props
  title = "CONFIRMATION",
  message = "Are you sure you want to proceed?",
  confirmText = "CONFIRM",
  cancelText = "CANCEL",
  onConfirm,
  onCancel,
  confirmButtonType = "primary", // primary, danger, warning
  icon = null,
  className = "",
  width = 520,
  centered = true,
  maskClosable = false,
  keyboard = true,
  destroyOnClose = true,
  zIndex = 1000,
  // Custom styling props
  titleColor = "#28398A",
  messageColor = "#65585B",
  confirmButtonColor = "#28398A",
  cancelButtonColor = "#ffffff",
  confirmButtonTextColor = "#ffffff",
  cancelButtonTextColor = "#28398A",
  borderRadius = "12px",
}) => {
  const handleConfirm = () => {
    if (onConfirm) {
      onConfirm();
    }
  };

  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    }
  };

  // Dynamic button styles based on type
  const getConfirmButtonStyle = () => {
    const baseStyle = {
      borderRadius: "8px",
      fontWeight: "600",
      fontSize: "14px",
      height: "44px",
      minWidth: "120px",
      border: "none",
      textTransform: "uppercase",
      letterSpacing: "0.5px",
    };

    switch (confirmButtonType) {
      case "danger":
        return {
          ...baseStyle,
          backgroundColor: "#28398A",
          color: "#ffffff",
        };
      case "warning":
        return {
          ...baseStyle,
          backgroundColor: "#28398A",
          color: "#ffffff",
        };
      case "success":
        return {
          ...baseStyle,
          backgroundColor: "#28398A",
          color: "#ffffff",
        };
      default: // primary
        return {
          ...baseStyle,
          backgroundColor: confirmButtonColor,
          color: confirmButtonTextColor,
        };
    }
  };

  const getCancelButtonStyle = () => {
    return {
      borderRadius: "8px",
      fontWeight: "600",
      fontSize: "14px",
      height: "44px",
      minWidth: "120px",
      backgroundColor: cancelButtonColor,
      color: cancelButtonTextColor,
      border: `1px solid #28398A`,
      textTransform: "uppercase",
      letterSpacing: "0.5px",
    };
  };

  // Use open prop if provided, otherwise fall back to visible
  const isOpen = open !== undefined ? open : visible;

  // Debug: console.log('ConfirmationPopup render - isOpen:', isOpen, 'visible:', visible);

  // Don't render anything if not open
  if (!isOpen) {
    return null;
  }

  return (
    <Modal
      title={null}
      open={true} // Always true when component renders
      onCancel={handleCancel}
      className={`confirmation-popup ${className}`}
      width={width}
      centered={centered}
      maskClosable={maskClosable}
      keyboard={keyboard}
      destroyOnClose={destroyOnClose}
      zIndex={zIndex}
      footer={null}
      closable={true}
      transitionName=""
      maskTransitionName=""
      closeIcon={
        <span style={{
          fontSize: '16px',
          color: '#7e8299',
          fontWeight: 'bold'
        }}>
          ×
        </span>
      }
      style={{
        borderRadius: borderRadius,
      }}
    >
      <div className="confirmation-popup-content">
        {/* Header */}
        <div className="confirmation-popup-header">
          {icon && <div className="confirmation-popup-icon">{icon}</div>}
          <h2
            className="confirmation-popup-title"
            style={{ color: titleColor }}
          >
            {title}
          </h2>
        </div>

        {/* Message */}
        <div className="confirmation-popup-message">
          <p style={{ color: messageColor }}>
            {message}
          </p>
        </div>

        {/* Footer with buttons */}
        <div className="confirmation-popup-footer">
          <button
            className="confirmation-popup-cancel-btn"
            style={getCancelButtonStyle()}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleCancel();
            }}
            type="button"
            tabIndex={0}
          >
            {cancelText}
          </button>
          <button
            className="confirmation-popup-confirm-btn"
            style={getConfirmButtonStyle()}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleConfirm();
            }}
            type="button"
            tabIndex={0}
          >
            {confirmText}
          </button>
        </div>
      </div>
    </Modal>
  );
};

// Predefined configurations for common use cases
export const showDeleteConfirmation = (itemName, onConfirm, onCancel) => {
  return {
    title: "CONFIRMATION",
    message: `Are you sure you want to delete ${itemName}?`,
    confirmText: "DELETE",
    cancelText: "CANCEL",
    confirmButtonType: "danger",
    onConfirm,
    onCancel,
  };
};

export const showApproveConfirmation = (itemName, onConfirm, onCancel) => {
  return {
    title: "CONFIRMATION",
    message: `Are you sure you want to approve ${itemName}?`,
    confirmText: "APPROVE",
    cancelText: "CANCEL",
    confirmButtonType: "success",
    onConfirm,
    onCancel,
  };
};

export const showRejectConfirmation = (itemName, onConfirm, onCancel) => {
  return {
    title: "CONFIRMATION",
    message: `Are you sure you want to reject ${itemName}?`,
    confirmText: "REJECT",
    cancelText: "CANCEL",
    confirmButtonType: "danger",
    onConfirm,
    onCancel,
  };
};

export const showUpdateConfirmation = (itemName, onConfirm, onCancel) => {
  return {
    title: "CONFIRMATION",
    message: `Are you sure you want to update ${itemName}?`,
    confirmText: "UPDATE",
    cancelText: "CANCEL",
    confirmButtonType: "primary",
    onConfirm,
    onCancel,
  };
};

export const showSaveConfirmation = (itemName, onConfirm, onCancel) => {
  return {
    title: "CONFIRMATION",
    message: `Are you sure you want to save ${itemName}?`,
    confirmText: "SAVE",
    cancelText: "CANCEL",
    confirmButtonType: "primary",
    onConfirm,
    onCancel,
  };
};

export const showSubmitConfirmation = (itemName, onConfirm, onCancel) => {
  return {
    title: "CONFIRMATION",
    message: `Are you sure you want to submit ${itemName}?`,
    confirmText: "SUBMIT",
    cancelText: "CANCEL",
    confirmButtonType: "primary",
    onConfirm,
    onCancel,
  };
};

export const showDeactivateConfirmation = (itemName, onConfirm, onCancel) => {
  return {
    title: "CONFIRMATION",
    message: `Are you sure you want to deactivate ${itemName}?`,
    confirmText: "DEACTIVATE",
    cancelText: "CANCEL",
    confirmButtonType: "warning",
    onConfirm,
    onCancel,
  };
};

export default ConfirmationPopup;
